import { useCallback, useMemo, useRef, useState } from 'react';
import { haptics } from '@/lib/haptics';
import { toast } from '@/lib/toast';
import type { UserItemImage } from '@/types/userItem';

export type OrderableImage =
  | { type: 'uploaded'; index: number; url: string }
  | { type: 'stock'; url: string };

/**
 * Owns every piece of image state used by ItemFormModal:
 * add-mode uploads, edit-mode existing images + pending deletions,
 * stock-photo positioning, drag/drop + touch reorder, and the
 * in-modal image editor dialog. Purely self-contained — no form,
 * user, or item deps leak in; the caller wires the returned handlers
 * into JSX and calls `initForEdit` / `resetForAdd` from its own
 * modal-open effect.
 */
export function useItemFormImages(maxImages = 10) {
  // ---- Add / edit image state ----
  const [newImages, setNewImages] = useState<File[]>([]);
  const [newImagePreviews, setNewImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<UserItemImage[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [photoOrderDirty, setPhotoOrderDirty] = useState(false);

  // ---- Stock photo ----
  const [stockImageUrl, setStockImageUrl] = useState<string | null>(null);
  const [stockPosition, setStockPosition] = useState<number | null>(null);

  // ---- Image editor dialog ----
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [editingImageSrc, setEditingImageSrc] = useState<string | null>(null);

  // ---- Delete confirmation ----
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [imageToRemove, setImageToRemove] = useState<
    { type: 'existing' | 'new'; id: string; index?: number } | null
  >(null);

  // ---- Drag / touch state ----
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [touchDragIndex, setTouchDragIndex] = useState<number | null>(null);
  const [hasDragged, setHasDragged] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  // ---- Drop zone state ----
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const photosDragCounter = useRef(0);

  // ---- Camera input ref (consumed by JSX) ----
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // ---- Derived counts ----
  const visibleExistingImages = useMemo(
    () => existingImages.filter((img) => !imagesToDelete.includes(img.id)),
    [existingImages, imagesToDelete],
  );
  const uploadedImageCount = visibleExistingImages.length + newImages.length;
  const hasStockImage = Boolean(stockImageUrl);
  const totalImageCount = uploadedImageCount + (hasStockImage ? 1 : 0);

  // ---- Orderable list (uploads + stock at its position) ----
  const orderableImages = useMemo((): OrderableImage[] => {
    const uploaded: OrderableImage[] = newImagePreviews.map((url, i) => ({
      type: 'uploaded' as const,
      index: i,
      url,
    }));
    if (!stockImageUrl) return uploaded;
    const stockItem: OrderableImage = { type: 'stock' as const, url: stockImageUrl };
    const effectivePos =
      stockPosition !== null && stockPosition >= 0 && stockPosition <= uploaded.length
        ? stockPosition
        : uploaded.length;
    const result = [...uploaded];
    result.splice(effectivePos, 0, stockItem);
    return result;
  }, [newImagePreviews, stockImageUrl, stockPosition]);

  // ---- File intake ----
  const addRawFiles = useCallback(
    async (rawFiles: File[]) => {
      if (rawFiles.length === 0) return;
      if (totalImageCount + rawFiles.length > maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }
      let files: File[];
      try {
        const { processSelectedFiles } = await import('@/lib/photoInputUtils');
        files = await processSelectedFiles(rawFiles, {
          maxCount: maxImages - totalImageCount,
        });
        if (files.length === 0) return;
      } catch (err: any) {
        toast.error(err?.message || 'One of the images could not be read.');
        return;
      }
      const maxNewImages = maxImages - visibleExistingImages.length;
      setNewImages((prev) => {
        const combined = [...prev, ...files].slice(0, maxNewImages);
        setNewImagePreviews(combined.map((f) => URL.createObjectURL(f)));
        return combined;
      });
    },
    [totalImageCount, maxImages, visibleExistingImages.length],
  );

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const rawFiles = Array.from(input.files || []);
      input.value = '';
      await addRawFiles(rawFiles);
    },
    [addRawFiles],
  );

  // ---- Drop-zone drag ----
  const handlePhotosDragEnter = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    photosDragCounter.current += 1;
    setIsDraggingPhotos(true);
  }, []);
  const handlePhotosDragOver = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);
  const handlePhotosDragLeave = useCallback((e: React.DragEvent) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    photosDragCounter.current = Math.max(0, photosDragCounter.current - 1);
    if (photosDragCounter.current === 0) setIsDraggingPhotos(false);
  }, []);
  const handlePhotosDrop = useCallback(
    async (e: React.DragEvent) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      photosDragCounter.current = 0;
      setIsDraggingPhotos(false);
      await addRawFiles(Array.from(e.dataTransfer.files));
    },
    [addRawFiles],
  );

  // ---- Remove ----
  const removeNewImage = useCallback((index: number) => {
    setNewImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setNewImagePreviews(updated.map((f) => URL.createObjectURL(f)));
      return updated;
    });
  }, []);

  const handleRemoveExistingImage = useCallback((imageId: string) => {
    setImageToRemove({ type: 'existing', id: imageId });
    setConfirmDeleteOpen(true);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    setImageToRemove((current) => {
      if (current?.type === 'existing') {
        setImagesToDelete((prev) => [...prev, current.id]);
      }
      return null;
    });
    setConfirmDeleteOpen(false);
  }, []);

  const moveExistingImage = useCallback(
    (imageId: string, direction: -1 | 1) => {
      setExistingImages((prev) => {
        const visible = prev.filter((img) => !imagesToDelete.includes(img.id));
        const fromIdx = visible.findIndex((img) => img.id === imageId);
        const toIdx = fromIdx + direction;
        if (fromIdx < 0 || toIdx < 0 || toIdx >= visible.length) return prev;
        const reorderedVisible = [...visible];
        const [moved] = reorderedVisible.splice(fromIdx, 1);
        reorderedVisible.splice(toIdx, 0, moved);
        const deleted = prev.filter((img) => imagesToDelete.includes(img.id));
        setPhotoOrderDirty(true);
        return [...reorderedVisible, ...deleted];
      });
    },
    [imagesToDelete],
  );

  // ---- Reorder within orderable list (mouse) ----
  const handleDragStart = useCallback((orderIndex: number) => {
    setDragIndex(orderIndex);
    setHasDragged(true);
  }, []);

  const reorderUploadedIntoTarget = useCallback(
    (fromUploadedIdx: number, uploadedTargetIdx: number) => {
      setNewImagePreviews((prevPreviews) => {
        const allPreviews = [...prevPreviews];
        const draggedPreview = allPreviews[fromUploadedIdx];
        allPreviews.splice(fromUploadedIdx, 1);
        const adjustedTarget =
          fromUploadedIdx < uploadedTargetIdx ? uploadedTargetIdx - 1 : uploadedTargetIdx;
        allPreviews.splice(adjustedTarget < 0 ? 0 : adjustedTarget, 0, draggedPreview);
        return allPreviews;
      });
      setNewImages((prevFiles) => {
        const allFiles = [...prevFiles];
        const draggedFile = allFiles[fromUploadedIdx];
        allFiles.splice(fromUploadedIdx, 1);
        const adjustedTarget =
          fromUploadedIdx < uploadedTargetIdx ? uploadedTargetIdx - 1 : uploadedTargetIdx;
        allFiles.splice(adjustedTarget < 0 ? 0 : adjustedTarget, 0, draggedFile);
        return allFiles;
      });
      setStockPosition((sp) => {
        if (sp === null) return sp;
        const adjustedTarget =
          fromUploadedIdx < uploadedTargetIdx ? uploadedTargetIdx - 1 : uploadedTargetIdx;
        if (fromUploadedIdx < sp && adjustedTarget >= sp) return sp - 1;
        if (fromUploadedIdx > sp && adjustedTarget <= sp) return sp + 1;
        return sp;
      });
    },
    [],
  );

  const computeUploadedTargetIdx = useCallback(
    (targetOrderIndex: number, list: OrderableImage[]) => {
      let uploadedTargetIdx = 0;
      let countUploaded = 0;
      for (let i = 0; i <= targetOrderIndex && i < list.length; i++) {
        if (list[i].type === 'uploaded') {
          if (i === targetOrderIndex) {
            uploadedTargetIdx = countUploaded;
            break;
          }
          countUploaded++;
        } else if (i === targetOrderIndex) {
          uploadedTargetIdx = countUploaded;
          break;
        }
      }
      return uploadedTargetIdx;
    },
    [],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, targetOrderIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === targetOrderIndex) return;
      const draggedItem = orderableImages[dragIndex];
      const targetItem = orderableImages[targetOrderIndex];
      if (draggedItem.type === 'stock') {
        setStockPosition(targetOrderIndex);
        setDragIndex(targetOrderIndex);
      } else if (targetItem?.type === 'stock') {
        setStockPosition(targetOrderIndex);
        setDragIndex(targetOrderIndex);
      } else {
        const fromUploadedIdx = draggedItem.index;
        const uploadedTargetIdx = computeUploadedTargetIdx(targetOrderIndex, orderableImages);
        reorderUploadedIntoTarget(fromUploadedIdx, uploadedTargetIdx);
        setDragIndex(targetOrderIndex);
      }
    },
    [dragIndex, orderableImages, computeUploadedTargetIdx, reorderUploadedIntoTarget],
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setTimeout(() => setHasDragged(false), 50);
  }, []);

  // ---- Touch drag ----
  const handleTouchStart = useCallback((orderIndex: number, e: React.TouchEvent) => {
    touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    longPressTimer.current = setTimeout(() => {
      setTouchDragIndex(orderIndex);
      haptics.impact();
    }, 400);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchDragIndex === null && touchStartPos.current) {
        const dx = Math.abs(e.touches[0].clientX - touchStartPos.current.x);
        const dy = Math.abs(e.touches[0].clientY - touchStartPos.current.y);
        if (dx > 10 || dy > 10) {
          if (longPressTimer.current) clearTimeout(longPressTimer.current);
        }
      }
      if (touchDragIndex === null) return;

      const touch = e.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const imageContainer = element?.closest('[data-image-index]');
      if (!imageContainer) return;
      const targetOrderIndex = parseInt(
        imageContainer.getAttribute('data-image-index') || '',
        10,
      );
      if (isNaN(targetOrderIndex) || targetOrderIndex === touchDragIndex) return;

      const draggedItem = orderableImages[touchDragIndex];
      const targetItem = orderableImages[targetOrderIndex];
      if (draggedItem?.type === 'stock') {
        setStockPosition(targetOrderIndex);
        setTouchDragIndex(targetOrderIndex);
      } else if (targetItem?.type === 'stock') {
        setStockPosition(touchDragIndex);
        setTouchDragIndex(targetOrderIndex);
      } else if (draggedItem?.type === 'uploaded') {
        const fromUploadedIdx = draggedItem.index;
        const uploadedTargetIdx = computeUploadedTargetIdx(targetOrderIndex, orderableImages);
        reorderUploadedIntoTarget(fromUploadedIdx, uploadedTargetIdx);
        setTouchDragIndex(targetOrderIndex);
      }
    },
    [touchDragIndex, orderableImages, computeUploadedTargetIdx, reorderUploadedIntoTarget],
  );

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    setTouchDragIndex(null);
    touchStartPos.current = null;
  }, []);

  // ---- Uploaded removal (adjusts stockPosition) ----
  const handleRemoveUploadedImage = useCallback(
    (uploadedIndex: number) => {
      removeNewImage(uploadedIndex);
      setStockPosition((sp) =>
        sp !== null && sp > uploadedIndex ? sp - 1 : sp,
      );
    },
    [removeNewImage],
  );

  const handleRemoveStockImage = useCallback(() => {
    setStockImageUrl(null);
    setStockPosition(null);
  }, []);

  // ---- Image editor ----
  const handleImageClick = useCallback(
    (src: string, index: number) => {
      if (!hasDragged && touchDragIndex === null && dragIndex === null) {
        setEditingImageSrc(src);
        setEditingImageIndex(index);
      }
    },
    [hasDragged, touchDragIndex, dragIndex],
  );

  const closeEditor = useCallback(() => {
    setEditingImageIndex(null);
    setEditingImageSrc(null);
  }, []);

  const handleImageEdited = useCallback(
    (blob: Blob) => {
      setEditingImageIndex((idx) => {
        if (idx === null) return null;
        const existingCount = visibleExistingImages.length;
        const newFile = new File([blob], `edited-${Date.now()}.jpg`, { type: 'image/jpeg' });
        if (idx < existingCount) {
          const existingImg = visibleExistingImages[idx];
          setImagesToDelete((prev) => [...prev, existingImg.id]);
          setNewImages((prev) => {
            const next = [...prev, newFile];
            setNewImagePreviews(next.map((f) => URL.createObjectURL(f)));
            return next;
          });
        } else {
          const newIndex = idx - existingCount;
          setNewImages((prev) => {
            const updated = [...prev];
            updated[newIndex] = newFile;
            setNewImagePreviews(updated.map((f) => URL.createObjectURL(f)));
            return updated;
          });
        }
        setEditingImageSrc(null);
        return null;
      });
    },
    [visibleExistingImages],
  );

  // ---- Lifecycle helpers used by parent's open-effect ----
  const initForEdit = useCallback(
    (opts: { images: UserItemImage[]; stockImageUrl: string | null }) => {
      setExistingImages(opts.images);
      setImagesToDelete([]);
      setNewImages([]);
      setNewImagePreviews([]);
      setStockImageUrl(opts.stockImageUrl);
      setStockPosition(null);
      setPhotoOrderDirty(false);
    },
    [],
  );

  const resetForAdd = useCallback(() => {
    setNewImages([]);
    setNewImagePreviews([]);
    setExistingImages([]);
    setImagesToDelete([]);
    setStockImageUrl(null);
    setStockPosition(null);
    setPhotoOrderDirty(false);
  }, []);

  return {
    // state
    newImages,
    newImagePreviews,
    existingImages,
    imagesToDelete,
    stockImageUrl,
    stockPosition,
    photoOrderDirty,
    editingImageIndex,
    editingImageSrc,
    confirmDeleteOpen,
    dragIndex,
    touchDragIndex,
    isDraggingPhotos,
    cameraInputRef,
    // setters exposed for legacy call sites (Stock picker, FastAddRow)
    setNewImages,
    setNewImagePreviews,
    setStockImageUrl,
    setConfirmDeleteOpen,
    // derived
    visibleExistingImages,
    uploadedImageCount,
    hasStockImage,
    totalImageCount,
    orderableImages,
    // handlers
    addRawFiles,
    handleImageChange,
    handlePhotosDragEnter,
    handlePhotosDragOver,
    handlePhotosDragLeave,
    handlePhotosDrop,
    removeNewImage,
    handleRemoveExistingImage,
    confirmRemoveImage,
    moveExistingImage,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleRemoveUploadedImage,
    handleRemoveStockImage,
    handleImageClick,
    closeEditor,
    handleImageEdited,
    // lifecycle
    initForEdit,
    resetForAdd,
  };
}
